// The v3 autosave state machine: continuous, day-collapsed save of the sizing to Salesforce.
//
// A plain controller with every side effect injected (store, timers, network, clock), so the
// throttle, dirty check and flush paths are unit-testable without React or a DOM. The throttle
// model, failure classification and baseline rule are v2's (components/calculator/autosave.ts,
// reused as-is): first push ~30s after becoming dirty, then at most one push per 15 minutes,
// failures stay dirty and retry on that cadence, force-sync pushes immediately.
//
// What is new in v3:
//  - The dirty check fingerprints the whole sizing including every use case's realization and
//    attribution (state/save-request.ts), so a factor-only edit saves. v2's fingerprint missed it.
//  - Re-anchoring on `epoch` (hydrate / reset) and on account change replaces v2's imperative
//    `resetSyncBaseline` calls: loading a saved sizing, a share link or a session is never an edit.
//  - Demo mode has no linked account, so the machine is inert there and
//    nothing is ever written.

import type { SaveSnapshotBody } from '../api-client';
import {
  classifySaveFailure,
  nextBaselineAfterFlush,
  nextPushDelayMs,
} from '../components/calculator/autosave';
import { sizingOf, type CalculatorState } from './calculator-store';
import { sizingFingerprint, type LinkedAccountSelection } from './save-request';
import type { SyncState } from './autosave-store';

export interface AutosaveDeps {
  getState(): CalculatorState;
  subscribe(listener: (state: CalculatorState, prev: CalculatorState) => void): () => void;
  buildBody(account: LinkedAccountSelection, state: CalculatorState): SaveSnapshotBody;
  /** Resolves on 2xx, rejects otherwise (the throwing mini-app `apiFetch`). */
  push(body: SaveSnapshotBody): Promise<void>;
  /** The pagehide path: a keepalive POST. Resolves true only on a confirmed 2xx. */
  pushKeepalive(body: SaveSnapshotBody): Promise<boolean>;
  /** False while impersonating with writes locked: an unprompted flush would only 403. */
  flushAllowed(): boolean;
  now(): number;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(handle: unknown): void;
  setSync(patch: Partial<SyncState>): void;
  getSync(): SyncState;
}

export interface Autosave {
  start(): void;
  stop(): void;
  /** Push now, bypassing the throttle (the chip click, and manual retry after a failure). */
  forceSync(): void;
  /** Best-effort send of unsynced edits for the linked account, bypassing the throttle. */
  flushPending(opts?: { keepalive?: boolean }): void;
}

export function accountKey(account: CalculatorState['account']): string {
  if (!account) return '';
  if (account.kind === 'demo') return 'demo';
  return `${account.accountId}|${account.opportunityId ?? ''}`;
}

const linkedOf = (s: CalculatorState): LinkedAccountSelection | null => (s.account?.kind === 'account' ? s.account : null);

/** The slice references the cheap first-tier dirty gate compares. */
const sliceRefs = (s: CalculatorState) => [s.deal, s.selected, s.tools, s.customTools] as const;

export function createAutosave(deps: AutosaveDeps): Autosave {
  let baseline: string | null = null;
  let lastRefs: ReturnType<typeof sliceRefs> | null = null;
  let timer: unknown;
  let hasTimer = false;
  let inFlight = false;
  let lastAttemptAt = 0;
  let unsubscribe: (() => void) | null = null;

  const fingerprint = (s: CalculatorState) => sizingFingerprint(sizingOf(s));

  function cancelTimer() {
    if (hasTimer) deps.clearTimer(timer);
    hasTimer = false;
  }

  function schedule(delayMs: number) {
    cancelTimer();
    hasTimer = true;
    timer = deps.setTimer(() => {
      hasTimer = false;
      void push();
    }, delayMs);
  }

  /** Hydration is not an edit: anchor the baseline at the state as loaded. */
  function resetBaseline(s: CalculatorState, accountChanged: boolean) {
    cancelTimer();
    baseline = fingerprint(s);
    lastRefs = sliceRefs(s);
    deps.setSync({
      status: linkedOf(s) ? 'synced' : 'local-only',
      oauthUrl: null,
      ...(accountChanged ? { lastSyncedAt: null } : {}),
    });
  }

  function checkDirty(s: CalculatorState) {
    if (!linkedOf(s) || baseline === null) return;
    const refs = sliceRefs(s);
    if (lastRefs && refs.every((r, i) => r === lastRefs![i])) return; // nothing a save would write moved
    lastRefs = refs;
    if (fingerprint(s) !== baseline) {
      if (deps.getSync().status === 'synced') deps.setSync({ status: 'dirty' });
      if (!hasTimer && !inFlight) schedule(nextPushDelayMs(deps.now(), lastAttemptAt));
    } else if (deps.getSync().status === 'dirty' && !inFlight) {
      // Edited back to exactly what was last saved before the push fired: nothing to send.
      cancelTimer();
      deps.setSync({ status: 'synced' });
    }
  }

  async function push() {
    const s = deps.getState();
    const account = linkedOf(s);
    if (!account || inFlight) return;
    inFlight = true;
    lastAttemptAt = deps.now();
    // Capture the body and fingerprint now; edits landing mid-flight are detected afterwards.
    const fpAtCapture = fingerprint(s);
    const body = deps.buildBody(account, s);
    deps.setSync({ status: 'syncing' });
    try {
      await deps.push(body);
      // The account changed mid-flight: the switch already reset the machine for the new one.
      if (accountKey(deps.getState().account) !== accountKey(account)) return;
      baseline = fpAtCapture;
      if (fingerprint(deps.getState()) === fpAtCapture) {
        deps.setSync({ status: 'synced', lastSyncedAt: deps.now(), oauthUrl: null });
      } else {
        deps.setSync({ status: 'dirty', lastSyncedAt: deps.now(), oauthUrl: null });
        schedule(nextPushDelayMs(deps.now(), lastAttemptAt));
      }
    } catch (err) {
      if (accountKey(deps.getState().account) !== accountKey(account)) return;
      const failure = classifySaveFailure(err);
      deps.setSync(failure.kind === 'reauth' ? { status: 'reauth', oauthUrl: failure.oauthUrl } : { status: 'error', errorMessage: failure.message });
      // Stay dirty; retry on the same 15 minute cadence (force-sync doubles as manual retry).
      schedule(nextPushDelayMs(deps.now(), lastAttemptAt));
    } finally {
      inFlight = false;
    }
  }

  function flushPending(opts?: { keepalive?: boolean }, at?: { state: CalculatorState; account: LinkedAccountSelection | null }) {
    if (!deps.flushAllowed()) return;
    const s = at?.state ?? deps.getState();
    const account = at ? at.account : linkedOf(s);
    if (!account || inFlight || baseline === null) return;
    const f = fingerprint(s);
    if (f === baseline) return; // clean
    const body = deps.buildBody(account, s);
    // Advance the baseline only once the POST is KNOWN to have landed, and only if nothing
    // re-anchored it in the meantime (a push or an account switch is newer truth).
    const baselineAtFlush = baseline;
    const advance = () => {
      baseline = nextBaselineAfterFlush(baseline, baselineAtFlush, f);
    };
    if (opts?.keepalive) {
      deps
        .pushKeepalive(body)
        .then((ok) => {
          if (ok) advance();
        })
        .catch(() => {
          /* best-effort */
        });
    } else {
      deps
        .push(body)
        .then(advance)
        .catch(() => {
          /* best-effort */
        });
    }
  }

  function onChange(state: CalculatorState, prev: CalculatorState) {
    if (accountKey(state.account) !== accountKey(prev.account)) {
      // Unsynced edits belong to the account being left: flush them to it before re-anchoring.
      flushPending(undefined, { state: prev, account: linkedOf(prev) });
      resetBaseline(state, true);
      return;
    }
    // A clear (`clearedEpoch === epoch`) is an edit: it falls through to the dirty check.
    if (state.epoch !== prev.epoch && state.clearedEpoch !== state.epoch) {
      resetBaseline(state, false);
      return;
    }
    checkDirty(state);
  }

  return {
    start() {
      if (unsubscribe) return;
      const s = deps.getState();
      // First start anchors the baseline; a later start (the calculator remounting after the deck)
      // keeps it, so edits that never made it out are still seen as dirty.
      if (baseline === null) resetBaseline(s, true);
      else {
        lastRefs = null; // re-check from scratch: the timer was cancelled on stop
        checkDirty(s);
      }
      unsubscribe = deps.subscribe(onChange);
    },
    stop() {
      unsubscribe?.();
      unsubscribe = null;
      flushPending();
      cancelTimer();
    },
    forceSync() {
      cancelTimer();
      void push();
    },
    flushPending: (opts) => flushPending(opts),
  };
}
